import { isPauseInternetActiveAt } from '../checkers/pauseInternetRuleChecker';
import {
  findActiveRoutineRule,
  parseRoutineActiveUntilInSeconds,
  routineRuleChecker,
  RoutineRuleStateType,
} from '../checkers/routineRuleChecker';
import { ROUTINE_RULE_TYPE, type RoutineRule } from '../types';
import type { PauseInternetRuleStateStore, RulesStore } from '../types/store';
import { resolvePauseInternetEndsAtInSeconds } from '../helper/pauseInternet';
import { shouldCheckNavigation } from '../helper/rules';
import { createAlarmWithDelay, clearAlarm } from '../alarmUtils';
import { updateTabsStateIfNeeded } from '../chrome/tabManager';
import { getRulesStore, getTimeNotificationsStore } from '../storage/stores';
import { display } from '../notifications';
import { t } from '../helper/locale';
import { captureException } from '../helper/sentry';
import {
  getDateInFuture,
  getDateMs,
  getStartOfDay,
  nowImmediate,
} from '../lib/dateTime';
import { TimeDuration } from '../lib/timeDuration';
import { clearVerdictCache } from '../navigation/verdict';
import { getExtensionVersion } from '../chrome/api';
import { onNewDay } from '../screenTime';

declare const isDelegation: boolean;

export const PAUSE_INTERNET_EXPIRES_ALARM_NAME = 'PAUSE_INTERNET_EXPIRES_ALARM';
/**
 * Fires while any routine is
 * active so the verdict cache and tabs can be re-evaluated when it ends.
 */
export const ROUTINE_ACTIVE_ALARM = 'ROUTINE_ACTIVE_ALARM';
/** Fires before the next routine starts. */
export const ROUTINE_EXPECTED_SOON_ALARM = 'ROUTINE_EXPECTED_SOON_ALARM';
/**
 * Fires at the next local midnight so daily-limit blocked tabs are
 * re-evaluated when the day rolls over. Family has no screen-time day-change
 * trigger (it only runs under delegation), so without this alarm previously
 * blocked tabs are never reloaded after the quota resets.
 */
export const DAILY_QUOTA_DAY_CHANGE_ALARM = 'DAILY_QUOTA_DAY_CHANGE_ALARM';

const PAUSE_INTERNET_NOTIFICATION_ID = 'PAUSE_INTERNET_ACTIVATED_NOTIFICATION';
/**
 * Single notification slot for every routine event (15-/5-minute pre-start
 * and activation). Reusing the same id makes the OS replace the earlier
 * notification in place, so the user sees a coherent timeline instead of
 * three stacked toasts.
 */
const ROUTINE_NOTIFICATION_ID = 'ROUTINE_NOTIFICATION';

const ALARM_MIN_DELAY_MINUTES = 0.5;

const SHORT_DELAY_FALLBACK_MS = 30_000;
const FIFTEEN_MINUTES_IN_SECONDS = 15 * 60;
const FIVE_MINUTES_IN_SECONDS = 5 * 60;

const ROUTINE_PRE_START_VARIANT = {
  FIFTEEN_MINUTES: '15min',
  FIVE_MINUTES: '5min',
} as const;

type RoutineWakeUpAlarmName =
  | typeof ROUTINE_ACTIVE_ALARM
  | typeof ROUTINE_EXPECTED_SOON_ALARM;

type RoutinePreStartVariant =
  (typeof ROUTINE_PRE_START_VARIANT)[keyof typeof ROUTINE_PRE_START_VARIANT];

/**
 * Pre-start window matching {@link RoutineRuleStateType.NO_ROUTINE_ACTIVE_BUT_EXPECTED_SOON}
 * tolerance bands. KASA uses 13–15 and 3–5 to absorb scheduling jitter; we
 * mirror the same widths so a slightly delayed alarm does not skip the
 * notification entirely.
 */
const PRE_START_15_MIN_RANGE = {
  minSeconds: 13 * 60,
  maxSeconds: FIFTEEN_MINUTES_IN_SECONDS,
};
const PRE_START_5_MIN_RANGE = {
  minSeconds: 3 * 60,
  maxSeconds: FIVE_MINUTES_IN_SECONDS,
};

const runRuleHandler = async (
  handlerName: string,
  handler: () => Promise<void>
): Promise<void> => {
  try {
    await handler();
  } catch (error) {
    captureException(error as Error, {
      tags: { ruleHandler: handlerName },
    });
  }
};

/**
 * Single dispatcher invoked from `engineActions::syncRules` after a
 * `RulesStore` update has been persisted. Each rule type registers its own
 * private handler below; new rule-driven side effects (routines, daily quota,
 * …) should be added here so callers do not need to know about every
 * individual rule.
 *
 * When more than one handler exists, isolate them so a failure in one rule
 * does not prevent the others from running (e.g. wrap each handler in its
 * own try/catch reporting via `captureException`).
 *
 * @param prev - The rules snapshot before the update (may be `undefined`
 * on first sync after install).
 * @param next - The rules snapshot just persisted.
 */
export const onRulesUpdated = async (
  prev: RulesStore | undefined,
  next: RulesStore
): Promise<void> => {
  await runRuleHandler('pauseInternet', () =>
    handlePauseInternetUpdate(prev, next)
  );
  await runRuleHandler('routines', () => handleRoutinesUpdate(prev, next));
  await runRuleHandler('dailyQuotaDayChange', () =>
    reconcileDayChangeFromRules(next, nowImmediate())
  );
};

export const applyCurrentRulesActionsFromStore = async (): Promise<void> => {
  await runRuleHandler('pauseInternet', () => {
    const nowMs = nowImmediate();
    const rules = getRulesStore().get();
    return reconcilePauseInternetFromRules(rules, nowMs);
  });
  await runRuleHandler('routines', () => {
    const nowMs = nowImmediate();
    const rules = getRulesStore().get();
    return reconcileRoutinesFromRules(rules, nowMs);
  });
  await runRuleHandler('dailyQuotaDayChange', () =>
    reconcileDayChangeFromRules(getRulesStore().get(), nowImmediate())
  );
};

/**
 * Forces one full refresh when the running version differs from the version
 * that produced the persisted snapshot. QAPI replies 304 for unchanged policy,
 * so without this a new build keeps enforcing rules parsed by the old version
 * (e.g. missing routines). A missing value (older snapshot or fresh install)
 * counts as a mismatch.
 */
export const shouldForceRulesRefreshAfterExtensionUpdate = (): boolean =>
  getRulesStore().get()?.lastSyncedExtensionVersion !== getExtensionVersion();

export const shouldForceRulesRefreshForRoutineAlarm = (): boolean => {
  const rules = getRulesStore().get();
  if (!rules) {
    return false;
  }

  const nowInSeconds = Math.floor(nowImmediate() / 1000);
  const hasExpiredActiveRoutine = (rules.routines ?? []).some(routine => {
    const activeUntilInSeconds = parseRoutineActiveUntilInSeconds(
      routine.activeUntil
    );
    return activeUntilInSeconds > 0 && activeUntilInSeconds <= nowInSeconds;
  });
  if (hasExpiredActiveRoutine) {
    return true;
  }

  const nextStart = rules.nextScreenFreeTimeRoutineStartsAtInSeconds;
  return nextStart !== undefined && nextStart <= nowInSeconds;
};

/**
 * Service worker entry point invoked when `PAUSE_INTERNET_EXPIRES_ALARM`
 * fires. By the time the alarm runs, the local rule checker already returns
 * `notPaused` (because it compares `endsAtInSeconds` against the current
 * trusted time), so we just need to nudge any blocker tab to re-evaluate its
 * verdict.
 */
export const onPauseInternetExpired = async (): Promise<void> => {
  await updateTabsStateIfNeeded('pause-expired');
};

export const onRoutineExpired = async (): Promise<void> => {
  // Content-filtering routines can leave cached per-URL verdicts behind after
  // time passes. Pause does not need this because it is checked before the
  // verdict cache and its blocker verdict is never stored there.
  clearVerdictCache();
  await updateTabsStateIfNeeded('routine-expired');
  await syncRoutineExpiryAlarmFromStore();
};

export const syncRoutineExpiryAlarmFromStore = async (): Promise<void> => {
  await reconcileRoutinesFromRules(getRulesStore().get(), nowImmediate());
};

/**
 * Service worker entry point invoked when {@link DAILY_QUOTA_DAY_CHANGE_ALARM}
 * fires. The day rolled over, so any cached daily-limit verdict is stale:
 * clear the verdict cache, refresh tabs (handled by `onNewDay`) and re-arm the
 * alarm for the following midnight.
 */
export const onDayChange = async (): Promise<void> => {
  clearVerdictCache();
  await onNewDay();
  await reconcileDayChangeFromRules(getRulesStore().get(), nowImmediate());
};

/**
 * A daily-limit block can only happen when navigation is checked and at least
 * one quota window (weekdays/weekend) is enabled; otherwise `parseDailyQuotas`
 * leaves every day unlimited and the quota checker never blocks. Gating the
 * alarm on this avoids waking the service worker every midnight on devices
 * without a daily limit.
 */
const canDailyLimitBlock = (rules: RulesStore): boolean =>
  shouldCheckNavigation(rules.timeRestrictions) &&
  (rules.timeRestrictions.is_weekdays_quota ||
    rules.timeRestrictions.is_weekend_quota);

/**
 * (Re)schedules the day-change alarm for the next local midnight while a daily
 * limit could block, clearing it otherwise. Runs on every rules update and
 * service worker boot (via `applyCurrentRulesActionsFromStore`) so the alarm
 * survives restarts.
 */
const reconcileDayChangeFromRules = async (
  rules: RulesStore | undefined,
  nowMs: number
): Promise<void> => {
  if (!rules || !canDailyLimitBlock(rules)) {
    await clearAlarm(DAILY_QUOTA_DAY_CHANGE_ALARM);
    return;
  }

  await scheduleAlarmInSeconds({
    name: DAILY_QUOTA_DAY_CHANGE_ALARM,
    delayInSeconds: getSecondsUntilNextMidnight(nowMs, rules.timezone),
    fallback: () => {
      void runRuleHandler('dailyQuotaDayChange', onDayChange);
    },
    clampPastToMinDelay: true,
  });
};

const getSecondsUntilNextMidnight = (
  nowMs: number,
  timezone?: string
): number => {
  const nextMidnightMs = getDateMs(
    getStartOfDay(getDateInFuture({ days: 1 }, nowMs, timezone))
  );
  return Math.floor((nextMidnightMs - nowMs) / 1000);
};

const getPauseInternetRule = (
  rules: RulesStore | undefined
): PauseInternetRuleStateStore | undefined => {
  const pause = rules?.timeRestrictions.pause_internet;
  if (!pause) {
    return undefined;
  }
  return { endsAtInSeconds: resolvePauseInternetEndsAtInSeconds(pause) };
};

const reconcilePauseInternetFromRules = async (
  rules: RulesStore | undefined,
  nowMs: number
): Promise<void> => {
  const pauseInternetRule = getPauseInternetRule(rules);
  const isActive = isPauseInternetActiveAt(pauseInternetRule, nowMs);
  if (!isActive) {
    await clearAlarm(PAUSE_INTERNET_EXPIRES_ALARM_NAME);
    return;
  }

  const endsAtInSeconds = pauseInternetRule?.endsAtInSeconds ?? 0;
  await schedulePauseInternetExpiryAlarm(endsAtInSeconds, nowMs);
  await notifyOnPauseActivatedIfNeeded(endsAtInSeconds, nowMs);
};

const reconcileRoutinesFromRules = async (
  rules: RulesStore | undefined,
  nowMs: number
): Promise<void> => {
  const activeRoutine = findActiveRoutineRule(rules?.routines, nowMs);

  await notifyOnRoutineAlarm(activeRoutine);
  await syncRoutineAlarmFromRules(rules, nowMs, activeRoutine);
};

const notifyOnRoutineAlarm = async (
  activeRoutine: RoutineRule | undefined
): Promise<void> => {
  if (activeRoutine) {
    await notifyOnRoutineActivatedIfNeeded(undefined, activeRoutine);
    return;
  }

  const hasShown15MinNotification = await maybeNotifyRoutinePreStart(
    ROUTINE_PRE_START_VARIANT.FIFTEEN_MINUTES
  );
  if (!hasShown15MinNotification) {
    await maybeNotifyRoutinePreStart(ROUTINE_PRE_START_VARIANT.FIVE_MINUTES);
  }
};

const syncRoutineAlarmFromRules = async (
  rules: RulesStore | undefined,
  nowMs: number,
  activeRoutine: RoutineRule | undefined = findActiveRoutineRule(
    rules?.routines,
    nowMs
  ),
  shouldClearWhenNoNextStart = true
): Promise<void> => {
  if (activeRoutine) {
    await scheduleRoutineExpiryAlarm(
      parseRoutineActiveUntilInSeconds(activeRoutine.activeUntil),
      nowMs
    );
    return;
  }

  await scheduleNextRoutineAlarm(rules, nowMs, shouldClearWhenNoNextStart);
};

const scheduleNextRoutineAlarm = async (
  rules: RulesStore | undefined,
  nowMs: number,
  shouldClearWhenNoNextStart: boolean
): Promise<void> => {
  const nextStart = rules?.nextScreenFreeTimeRoutineStartsAtInSeconds;
  if (nextStart === undefined) {
    if (shouldClearWhenNoNextStart) {
      await clearRoutineWakeUpAlarms();
    }
    return;
  }

  const startsInSeconds = nextStart - Math.floor(nowMs / 1000);
  if (startsInSeconds <= 0) {
    await clearRoutineWakeUpAlarms();
    return;
  }

  const alarmName = getNextRoutineWakeUpAlarmName(startsInSeconds);
  await clearOtherRoutineWakeUpAlarm(alarmName);
  await scheduleAlarmInSeconds({
    name: alarmName,
    delayInSeconds: getNextRoutineWakeUpDelayInSeconds(startsInSeconds),
    fallback: getRoutineWakeUpFallback(alarmName),
    clampPastToMinDelay: true,
  });
};

const clearRoutineWakeUpAlarms = async (): Promise<void> => {
  await clearAlarm(ROUTINE_ACTIVE_ALARM);
  await clearAlarm(ROUTINE_EXPECTED_SOON_ALARM);
};

const clearOtherRoutineWakeUpAlarm = async (
  alarmName: RoutineWakeUpAlarmName
): Promise<void> => {
  await clearAlarm(
    alarmName === ROUTINE_ACTIVE_ALARM
      ? ROUTINE_EXPECTED_SOON_ALARM
      : ROUTINE_ACTIVE_ALARM
  );
};

const getNextRoutineWakeUpAlarmName = (
  startsInSeconds: number
): RoutineWakeUpAlarmName =>
  startsInSeconds > FIVE_MINUTES_IN_SECONDS
    ? ROUTINE_EXPECTED_SOON_ALARM
    : ROUTINE_ACTIVE_ALARM;

const getRoutineWakeUpFallback =
  (alarmName: RoutineWakeUpAlarmName): (() => void) =>
  () => {
    void (alarmName === ROUTINE_EXPECTED_SOON_ALARM
      ? syncRoutineExpiryAlarmFromStore()
      : onRoutineExpired());
  };

const getNextRoutineWakeUpDelayInSeconds = (
  startsInSeconds: number
): number => {
  if (startsInSeconds >= FIFTEEN_MINUTES_IN_SECONDS) {
    return startsInSeconds - FIFTEEN_MINUTES_IN_SECONDS;
  }

  if (startsInSeconds > FIVE_MINUTES_IN_SECONDS) {
    return startsInSeconds - FIVE_MINUTES_IN_SECONDS;
  }

  return startsInSeconds;
};

const hasConfiguredRoutineRules = (rules: RulesStore): boolean =>
  (rules.routines?.length ?? 0) > 0;

/**
 * Reacts to a Pause Internet rule transition.
 *
 * Transitions handled:
 * - Active pause: (re)schedule the expiry alarm and show a one-shot ChromeOS
 *   notification. The alarm is rescheduled whenever the pause is active,
 *   regardless of whether `endsAtInSeconds` changed.
 * - Cancellation: clear the alarm. Tab refresh is owned by the surrounding
 *   `syncRules` flow (`updateTabsStateIfNeeded('rules-refresh')`), so this
 *   handler deliberately does not duplicate it.
 * - No-op: pause never active on either side.
 *
 * Idempotency is enforced via `lastNotifiedPauseEndsAt` so service-worker
 * restarts that re-apply an already-active pause do not re-notify the user.
 */
const handlePauseInternetUpdate = async (
  prev: RulesStore | undefined,
  next: RulesStore
): Promise<void> => {
  const nowMs = nowImmediate();
  const prevPauseInternetRule = getPauseInternetRule(prev);
  const nextPauseInternetRule = getPauseInternetRule(next);

  const prevActive = isPauseInternetActiveAt(prevPauseInternetRule, nowMs);
  const nextActive = isPauseInternetActiveAt(nextPauseInternetRule, nowMs);

  if (!nextActive && prevActive) {
    await clearAlarm(PAUSE_INTERNET_EXPIRES_ALARM_NAME);
    return;
  }

  const nextEndsAtInSeconds = nextPauseInternetRule?.endsAtInSeconds ?? 0;
  if (!nextActive || nextEndsAtInSeconds <= 0) {
    return;
  }

  await schedulePauseInternetExpiryAlarm(nextEndsAtInSeconds, nowMs);
  await notifyOnPauseActivatedIfNeeded(nextEndsAtInSeconds, nowMs);
};

const schedulePauseInternetExpiryAlarm = (
  endsAtInSeconds: number,
  nowMs: number
): Promise<void> =>
  scheduleAlarmInSeconds({
    name: PAUSE_INTERNET_EXPIRES_ALARM_NAME,
    delayInSeconds: endsAtInSeconds - Math.floor(nowMs / 1000),
    fallback: () => {
      void onPauseInternetExpired();
    },
  });

/**
 * Single chrome.alarm scheduler shared by every rule-driven alarm
 * (pause expiry, routine active, routine pre-start).
 *
 * - Floors the delay to {@link ALARM_MIN_DELAY_MINUTES} because
 *   chrome.alarms silently ignores delays below 30 s.
 * - With `clampPastToMinDelay: true` a zero-delay wake-up at a canonical
 *   boundary is still scheduled at the minimum delay.
 *   Otherwise (default) a non-positive delay returns early so an
 *   already-expired pause / routine does not schedule a no-op alarm.
 * - Optionally arms a `setTimeout` fallback for sub-30 s windows so the
 *   side effect still fires before chrome.alarms catches up.
 */
const scheduleAlarmInSeconds = async ({
  name,
  delayInSeconds,
  fallback,
  clampPastToMinDelay = false,
}: {
  name: string;
  delayInSeconds: number;
  fallback?: () => void;
  clampPastToMinDelay?: boolean;
}): Promise<void> => {
  if (delayInSeconds <= 0 && !clampPastToMinDelay) {
    return;
  }

  const delayInMinutes = Math.max(delayInSeconds / 60, ALARM_MIN_DELAY_MINUTES);
  createAlarmWithDelay(name, delayInMinutes);

  if (!fallback) {
    return;
  }

  const delayInMs = delayInSeconds * 1000;
  if (delayInMs > 0 && delayInMs < SHORT_DELAY_FALLBACK_MS) {
    setTimeout(fallback, delayInMs);
  }
};

const handleRoutinesUpdate = async (
  prev: RulesStore | undefined,
  next: RulesStore
): Promise<void> => {
  const nowMs = nowImmediate();
  const prevActiveRoutine = findActiveRoutineRule(prev?.routines, nowMs);
  const nextActiveRoutine = findActiveRoutineRule(next.routines, nowMs);

  if (!nextActiveRoutine) {
    await syncRoutineAlarmFromRules(
      next,
      nowMs,
      undefined,
      prevActiveRoutine !== undefined || hasConfiguredRoutineRules(next)
    );
    return;
  }

  const nextExpiresAtInSeconds = parseRoutineActiveUntilInSeconds(
    nextActiveRoutine.activeUntil
  );

  await scheduleRoutineExpiryAlarm(nextExpiresAtInSeconds, nowMs);

  await notifyOnRoutineActivatedIfNeeded(prevActiveRoutine, nextActiveRoutine);
};

const scheduleRoutineExpiryAlarm = async (
  endsAtInSeconds: number,
  nowMs: number
): Promise<void> => {
  await clearAlarm(ROUTINE_EXPECTED_SOON_ALARM);
  await scheduleAlarmInSeconds({
    name: ROUTINE_ACTIVE_ALARM,
    delayInSeconds: endsAtInSeconds - Math.floor(nowMs / 1000),
    fallback: () => {
      void onRoutineExpired();
    },
  });
};

/**
 * Single side door for every routine ChromeOS notification
 * (pre-start 15', pre-start 5' and activation). Reusing one notification
 * id (`ROUTINE_NOTIFICATION_ID`) makes the OS replace earlier
 * toasts in place so the user sees one continuous timeline.
 *
 * Callers must gate on `isDelegation` upstream so that family devices
 * (where KASA owns the toast) keep their idempotency keys untouched.
 */
const displayRoutineNotification = (title: string, body: string): void => {
  display(ROUTINE_NOTIFICATION_ID, title, body);
};

/**
 * Re-checks the live routine state when a pre-start alarm fires. Falling
 * back to the checker (instead of the alarm timestamp) means cancellations
 * and reschedules between scheduling and firing become silent no-ops, and
 * delegation-only gating is enforced in a single place.
 */
const maybeNotifyRoutinePreStart = async (
  variant: RoutinePreStartVariant
): Promise<boolean> => {
  if (!isDelegation) {
    return false;
  }

  const state = routineRuleChecker.checkScreenFreeTime();
  if (
    state.state !== RoutineRuleStateType.NO_ROUTINE_ACTIVE_BUT_EXPECTED_SOON
  ) {
    return false;
  }

  const range =
    variant === ROUTINE_PRE_START_VARIANT.FIFTEEN_MINUTES
      ? PRE_START_15_MIN_RANGE
      : PRE_START_5_MIN_RANGE;
  if (
    state.startsInSeconds < range.minSeconds ||
    state.startsInSeconds > range.maxSeconds
  ) {
    return false;
  }

  const minutesLeft =
    variant === ROUTINE_PRE_START_VARIANT.FIFTEEN_MINUTES ? '15' : '5';
  const startsAtInSeconds =
    Math.floor(nowImmediate() / 1000) + state.startsInSeconds;
  const store = getTimeNotificationsStore();

  const lastNotified =
    variant === ROUTINE_PRE_START_VARIANT.FIFTEEN_MINUTES
      ? store.getLastNotifiedRoutinePreStart15Min()
      : store.getLastNotifiedRoutinePreStart5Min();
  if (lastNotified === startsAtInSeconds) {
    return false;
  }

  displayRoutineNotification(
    t('Time check'),
    t('{{minutesLeft}} minutes of screen time left today', {
      minutesLeft,
    })
  );

  if (variant === ROUTINE_PRE_START_VARIANT.FIFTEEN_MINUTES) {
    await store.setLastNotifiedRoutinePreStart15Min(startsAtInSeconds);
  } else {
    await store.setLastNotifiedRoutinePreStart5Min(startsAtInSeconds);
  }
  return true;
};

const notifyOnRoutineActivatedIfNeeded = async (
  prevActiveRoutine: RoutineRule | undefined,
  nextActiveRoutine: RoutineRule
): Promise<void> => {
  if (!isDelegation) {
    return;
  }

  const isNewRoutineActivation =
    nextActiveRoutine.type === ROUTINE_RULE_TYPE.SCREEN_FREE_TIME &&
    (prevActiveRoutine?.type !== ROUTINE_RULE_TYPE.SCREEN_FREE_TIME ||
      prevActiveRoutine.id !== nextActiveRoutine.id);
  if (!isNewRoutineActivation) {
    return;
  }

  const activeUntilInSeconds = parseRoutineActiveUntilInSeconds(
    nextActiveRoutine.activeUntil
  );
  const notificationKey = `${nextActiveRoutine.id}:${activeUntilInSeconds}`;
  const store = getTimeNotificationsStore();
  if (store.getLastNotifiedRoutineActivation() === notificationKey) {
    return;
  }

  displayRoutineNotification(
    t('Screen-free time'),
    t('You can use this device again later')
  );
  await store.setLastNotifiedRoutineActivation(notificationKey);
};

const notifyOnPauseActivatedIfNeeded = async (
  endsAtInSeconds: number,
  nowMs: number
): Promise<void> => {
  if (!isDelegation) {
    return;
  }

  const store = getTimeNotificationsStore();
  if (store.getLastNotifiedPauseEndsAt() === endsAtInSeconds) {
    return;
  }

  const endsAtMs = endsAtInSeconds * 1000;
  const remainingSeconds = Math.floor((endsAtMs - nowMs) / 1000);
  display(
    PAUSE_INTERNET_NOTIFICATION_ID,
    buildPauseNotificationTitle(remainingSeconds),
    t('It’s time to take a break from screens.')
  );
  await store.setLastNotifiedPauseEndsAt(endsAtInSeconds);
};

const buildPauseNotificationTitle = (remainingSeconds: number): string => {
  const duration = TimeDuration.fromSeconds(remainingSeconds);

  if (duration.kind === 'oneMinute') {
    return t('Internet paused for 1 minute');
  }

  if (duration.kind === 'oneHour') {
    return t('Internet paused for 1 hour');
  }

  if (duration.kind === 'minutes') {
    return t('Internet paused for {{minutes}} minutes', {
      minutes: String(duration.minutes),
    });
  }

  if (duration.kind === 'hours') {
    return t('Internet paused for {{hours}} hours', {
      hours: String(duration.hours),
    });
  }

  return t('Internet paused for {{hours}}h {{minutes}}m', {
    hours: String(duration.hours),
    minutes: String(duration.minutes),
  });
};
